import { useState } from 'react';

import { ModalForm } from '../../../design-system/patterns/modal-form';
import {
  PRICE_MOVED_TITLE,
  PRICE_MOVED_MESSAGE,
  PRICE_MOVED_CONTINUE_LABEL,
  PRICE_MOVED_CANCEL_LABEL,
  priceMovedDetail,
  PRICE_UNCONFIRMABLE_TITLE,
  PRICE_UNCONFIRMABLE_MESSAGE,
  PRICE_UNCONFIRMABLE_DISMISS_LABEL,
  confirmSubjectLabel,
} from '../../../utils/priceAvailability';

/**
 * Renders `useAddToCart`'s `confirm` descriptor (`hooks/useAddToCart.js`) --
 * the question a blocked add-to-cart owes the buyer instead of leaving it
 * silent. See that hook's "A BLOCKED ADD IS A QUESTION, NOT A DEAD END"
 * docstring for the three outcomes this answers, and its `@returns` JSDoc for
 * the descriptor's exact shape: `{ mode, listingId, oldPrice, newPrice,
 * cardName, condition, onContinue, onCancel }`, `mode` one of `'moved'` /
 * `'unconfirmable'`.
 *
 * WHY `ModalForm` DIRECTLY, NOT `ShareModal`, AND NOT `ConfirmForm` EITHER
 * (fix round 1). `ShareModal` wraps the legacy `UI/Modal`, which
 * `ConfirmationPopup`'s own docstring records as having NO FOCUS TRAP --
 * exactly what `design-system/patterns/modal-form` was built to retire (see
 * that file's "WHAT WENT AWAY", and `ConfirmForm`'s own use for "Delete
 * payment method" / "Delete address" in `Profile.view.jsx`). The first draft
 * of this component used `ConfirmForm` for 'moved' and a bare `ModalForm` for
 * 'unconfirmable' -- two DIFFERENT component types at the same tree position,
 * which is fine for React on a PROP change but not on a TYPE change: React
 * reconciles by type, so a mode-changing supersession (a 'moved' question
 * replaced by an 'unconfirmable' one, or the reverse -- reachable exactly the
 * way ruling 6's plain supersession is: two blocked tiles racing the same
 * deduped refresh, one row re-derived and confirmed, the other's own
 * (finish, condition) never priced, see `useAddToCart.js`'s "WHAT COUNTS AS
 * CONFIRMED") tore the whole `Modal`/`Sheet` subtree down and rebuilt it.
 * `Sheet.jsx`'s `restoreFocus` cleanup fired on the unmount, handing focus
 * back to the page behind the scrim, and the remount then stole it forward
 * again -- the loudest possible "swap", when ruling 6 asked for a silent one.
 * `ConfirmForm` is itself nothing but a thin wrapper over `ModalForm` (see
 * that file) that fixes `showCancel` to its default `true`, so nothing is
 * lost by inlining its two props here and choosing them per-mode instead:
 * ONE `<ModalForm>` element, at one tree position, for the life of the
 * component -- only its props and children change with `mode`.
 *
 * WHICH LISTING (fix round 1, IMPORTANT 2). `confirmSubjectLabel` renders as
 * `ModalForm`'s `subtitle` -- directly under the title, on both tracks,
 * before the buyer reads anything else -- because the failure mode was a
 * buyer who read only the SECOND of two superseding questions and answered
 * it believing it was the first tap they remembered making. See that
 * function's own docstring in `utils/priceAvailability.js` for the full
 * scenario and why `cardName`/`condition` cost no new request.
 *
 * ALWAYS MOUNTED, NEVER `{confirm && <PriceConfirmDialog .../>}`. Both
 * `Modal` and `Sheet` need to see `open` go from `true` to `false` to run
 * their own close behaviour -- `Sheet`'s docstring: "pulling the node out on
 * the closing tick is what left the form sheet with an entry animation and
 * no exit." `useAddToCart`'s `closeConfirm` sets `confirm` straight to
 * `null`, which would tear this component's content out from under that
 * transition, so the last non-null descriptor is kept in state and `open`
 * tracks `confirm` on its own -- the same shape `Profile.view.jsx` uses for
 * its own delete-confirmation dialogs (`addressToDelete` outlives the close).
 *
 * A SUPERSEDED QUESTION (ruling 6, task-3 brief). Two blocked tiles on the
 * same catalog card share one deduped refresh (`refreshCardPricesOnce`) and
 * so resolve in the same microtask; `openConfirm` cancels the outgoing
 * descriptor and opens the incoming one before either `setConfirm` call is
 * committed, so this component is handed the SECOND descriptor directly --
 * it never sees the first one's `onCancel` fire, and never renders `confirm:
 * null` in between. Nothing here has to special-case that swap: the dialog
 * simply re-renders with new content, which is what "cancelled silently"
 * means from this side -- and, as of this fix, stays the SAME rendered
 * dialog while it does, even when the mode itself changes.
 */
export default function PriceConfirmDialog({ confirm }) {
  const [lastConfirm, setLastConfirm] = useState(confirm);

  /* Adjusted DURING RENDER, not from an effect -- the same pattern `Sheet`
     itself uses for its `mounted` flag (`design-system/components/mobile/
     organisms/Sheet.jsx`). An effect would catch this one render late, and
     the close transition would already be playing against stale content. */
  if (confirm && confirm !== lastConfirm) {
    setLastConfirm(confirm);
  }

  const open = Boolean(confirm);

  /* Never opened yet: nothing to hold, nothing to close. */
  if (!lastConfirm) return null;

  const moved = lastConfirm.mode === 'moved';
  const subject = confirmSubjectLabel(lastConfirm.cardName, lastConfirm.condition);
  const priceDetail = moved ? priceMovedDetail(lastConfirm.oldPrice, lastConfirm.newPrice) : '';

  return (
    <ModalForm
      open={open}
      title={moved ? PRICE_MOVED_TITLE : PRICE_UNCONFIRMABLE_TITLE}
      subtitle={subject}
      /* ONE `onClose` for everything that is not the primary button: Escape,
         a backdrop click, and -- in 'moved' mode -- the Cancel button too
         (`ModalFormBody` wires its own Cancel to whatever `onCancel` it is
         given, which `ModalForm` sets to this same `onClose`). Ruling:
         "Both callbacks close the question; whichever the buyer presses is
         the single exit." -- true in both modes, so it is one prop here. */
      onClose={lastConfirm.onCancel}
      /* 'moved': the form's submit IS the question's live second answer,
         Continue. 'unconfirmable': there is only ever the one exit, so the
         form's submit action stands in for Dismiss rather than adding a
         second, redundant button -- deliberately no "add anyway"; see
         `useAddToCart`'s docstring for why an override here would only
         relocate the dead end from this tile to the checkout button. */
      onSubmit={moved ? lastConfirm.onContinue : lastConfirm.onCancel}
      submitLabel={moved ? PRICE_MOVED_CONTINUE_LABEL : PRICE_UNCONFIRMABLE_DISMISS_LABEL}
      cancelLabel={PRICE_MOVED_CANCEL_LABEL}
      /* Only 'moved' has a second, distinct answer worth its own button. */
      showCancel={moved}
    >
      <p className="dsa-mf__confirm">{moved ? PRICE_MOVED_MESSAGE : PRICE_UNCONFIRMABLE_MESSAGE}</p>
      {priceDetail ? <p className="dsa-mf__confirm-detail">{priceDetail}</p> : null}
    </ModalForm>
  );
}
